import {
  initChart,
  resultsetWithFields,
  printNoDataChart,
  printDataChart,
  formatShortMonthYear,
  formatDayMonthYear,
  subtractOneDay,
  subtractOneMonth,
} from 'vlcishared';
import { OPTIONS_INTERVENCIONES_TREE_MAP as options } from './options-charts';
import { COLORS } from '../colors';

const PORCENTAJE_MIN = 0.03

export function echartIntervencionesTreeMap(vis, datos, idChart, idNoData) {
  const chartOptions = prepararOpciones();
  const resultSet = resultsetWithFields(datos);

  if (resultSet.length === 0) {
    printNoDataChart(`#${idChart}`, `#${idNoData}`);
    return;
  }

  printDataChart(`#${idNoData}`, `#${idChart}`);

  const { mapNivel1, mapNivel2, mapNivel3 } = buildDataMaps(resultSet);
  buildJerarquia(mapNivel1, mapNivel2, mapNivel3);
  asignarColores(mapNivel1);

  const categoriasEscaladas = escalarCategorias(mapNivel1);
  chartOptions.series[0].data = categoriasEscaladas;

  actualizarSubtextos(chartOptions, idChart);
  const total = calcularTotalNivelCategoria(mapNivel1);
  setearFormatoLabel(chartOptions, mapNivel1, mapNivel2, total);

  initChart(idChart, chartOptions);
}

function escalarValores(nodos) {
  let total = nodos.reduce((sum, n) => sum + n.value, 0);
  const valorMin = total * PORCENTAJE_MIN;

  const ajustados = nodos.map((n) => ({
    ...n,
    ajustado: n.value < valorMin ? valorMin : n.value,
  }));

  // Nuevo total después de ajustar los valores menores al porcentajeMin
  const nuevoTotal = ajustados.reduce((sum, n) => sum + n.ajustado, 0);

  // Reescalamos todos los valores para que sumen 100%
  return ajustados.map((n) => ({
    ...n,
    value: (n.ajustado / nuevoTotal) * 100,
  }));
}

function escalarDetalleCategoria(subcategoria) {
  if (subcategoria.children && subcategoria.children.length > 0) {
    subcategoria.children = escalarValores(subcategoria.children);
  }
  return subcategoria;
}

function escalarSubcategorias(categoria) {
  if (categoria.children && categoria.children.length > 0) {
    categoria.children = categoria.children.map(escalarDetalleCategoria);
    categoria.children = escalarValores(categoria.children);
  }
  return categoria;
}

function escalarCategorias(data) {
  let categorias = Object.values(data);
  // Escalar segundo y tercer nivel primero
  categorias = categorias.map(escalarSubcategorias);
  // Escalar primer nivel
  categorias = escalarValores(categorias);
  return categorias;
}

function prepararOpciones() {
  const chartOptions = JSON.parse(JSON.stringify(options));
  chartOptions.tooltip.formatter = options.tooltip.formatter;
  return chartOptions;
}

function quitarNuloDeDetalleCategoria(detalle) {
  if (!detalle) return '';
  return detalle.replace(/ ?Nulo/i, '').trim();
}

function buildDataMaps(resultSet) {
  const mapNivel1 = {};
  const mapNivel2 = {};
  const mapNivel3 = {};

  resultSet.forEach((row) => {
    const detalleCategoriaLimpia = quitarNuloDeDetalleCategoria(
      row.detallecategoria,
    );

    if (!mapNivel1[row.categoria]) {
      mapNivel1[row.categoria] = {
        name: row.categoria,
        value: row.nivel1,
        originalValue: row.nivel1,
        children: [],
        itemStyle: { color: '' },
      };
    }

    const keyNivel2 = `${row.categoria}|${row.subcategoria}`;
    if (!mapNivel2[keyNivel2]) {
      mapNivel2[keyNivel2] = {
        categoria: row.categoria,
        name: row.subcategoria,
        value: row.nivel2,
        originalValue: row.nivel2,
        children: [],
      };
    }

    const keyNivel3 = `${row.categoria}|${row.subcategoria}|${detalleCategoriaLimpia}`;
    mapNivel3[keyNivel3] = {
      categoria: row.categoria,
      subcategoria: row.subcategoria,
      name: detalleCategoriaLimpia,
      value: row.nivel3,
      originalValue: row.nivel3,
    };
  });

  return { mapNivel1, mapNivel2, mapNivel3 };
}

function buildJerarquia(mapNivel1, mapNivel2, mapNivel3) {
  Object.values(mapNivel3).forEach((detail) => {
    const keyNivel2 = `${detail.categoria}|${detail.subcategoria}`;
    mapNivel2[keyNivel2]?.children.push({
      name: detail.name,
      value: detail.value,
      originalValue: detail.originalValue,
    });
  });

  Object.values(mapNivel2).forEach((sub) => {
    mapNivel1[sub.categoria]?.children.push({
      name: sub.name,
      value: sub.value,
      originalValue: sub.originalValue,
      children: sub.children,
    });
  });
}

function asignarColores(mapNivel1) {
  const colores = Object.values(COLORS);
  let index = 0;
  Object.values(mapNivel1).forEach((cat) => {
    cat.itemStyle.color = colores[index % colores.length];
    index++;
  });
}

function actualizarSubtextos(chartOptions, idChart) {
  if (idChart === 'chart_IntervencionesAnualTipo') {
    const paramUltimoMes = Dashboards.getParameterValue('paramUltimoMes');
    const ultimoMes = subtractOneMonth(paramUltimoMes);
    chartOptions.title.subtext = 'Ene - ' + formatShortMonthYear(ultimoMes);
  } else if (idChart === 'chart_IntervencionesMesTipo') {
    const paramUltimoDia = Dashboards.getParameterValue('paramUltimoDia');
    const ultimoDia = subtractOneDay(paramUltimoDia);
    chartOptions.title.subtext = '1' + '-' + formatDayMonthYear(ultimoDia);
  }
}

function calcularTotalNivelCategoria(mapNivel1) {
  return Object.values(mapNivel1).reduce(
    (sum, cat) => sum + cat.originalValue,
    0,
  );
}

function setearFormatoLabel(chartOptions, mapNivel1, mapNivel2, total) {
  chartOptions.series[0].label.formatter = function (params) {
    const treePathInfo = params.treePathInfo;
    const level = treePathInfo.length - 1;
    let totalPadreOriginal = total;

    if (level === 2) {
      const categoria = treePathInfo[1].name;
      totalPadreOriginal = mapNivel1[categoria]?.originalValue || total;
    } else if (level === 3) {
      const categoria = treePathInfo[1].name;
      const subcategoria = treePathInfo[2].name;
      const keyNivel2 = `${categoria}|${subcategoria}`;
      totalPadreOriginal = mapNivel2[keyNivel2]?.originalValue || total;
    }

    if (totalPadreOriginal > 0) {
      const porcentaje = (
        (params.data.originalValue / totalPadreOriginal) *
        100
      ).toFixed(2);
      const porcentajeTruncado = Math.min(porcentaje, 100);
      return `${params.name}\n${porcentajeTruncado}%`;
    }

    return params.name;
  };
}
